import React, { useState, useRef, useEffect } from "react";
import Modal from "react-modal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styles from "../../Components/LandingPage/BusinessesforSale/filter.module.css";
import { industriesData } from "../../Components/LandingPage/BusinessesforSale/industriesData";
import { updateBusinessForSale } from "../../Services/APIrequests";
import { SuccessSwal, warningSwal } from "../../Util/Toast";
import S3FileUpload from "react-s3";
import { config } from "../../Components/LandingPage/BusinessesforSale/config";
import useStore from "../../Store";

window.Buffer = window.Buffer || require("buffer").Buffer;
Modal.setAppElement("#root");

const EditBusinessModal = ({ business, onClose, onUpdated }) => {
    const { Theme } = useStore();
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [businessImages, setBusinessImages] = useState(
        Array.isArray(business.businessImages) ? business.businessImages : []
    );

    // Derive initial industry selections from the existing array
    const initCategory =
        Array.isArray(business.industry) && business.industry[0]
            ? business.industry[0]
            : "";
    const initSubcategory =
        Array.isArray(business.industry) && business.industry[1]
            ? business.industry[1]
            : "";
    const initItem =
        Array.isArray(business.industry) && business.industry[2]
            ? business.industry[2]
            : "";

    const [selectedCategory, setSelectedCategory] = useState(initCategory);
    const [selectedSubcategory, setSelectedSubcategory] = useState(initSubcategory);
    const [selectedItem, setSelectedItem] = useState(initItem);

    const [form, setForm] = useState({
        business_name: business.business_name || "",
        roc: business.roc || "",
        authorised_capital: business.authorised_capital || "",
        paid_up_capital: business.paid_up_capital || "",
        turn_over: business.turn_over || "",
        pat: business.pat || "",
        location: business.location || "",
        vintage_year: business.vintage_year || "",
        date_of_incorporation: business.date_of_incorporation
            ? business.date_of_incorporation.split("T")[0]
            : "",
        document: business.document || "",
        business_image: business.business_image || "",
        about_company: business.about_company || "",
        legal_entity: business.legal_entity || "",
        sale_price: business.sale_price || "",
        operation_type: business.operation_type || "",
        with_website: business.with_website !== undefined ? String(business.with_website) : "",
        state: business.state || "",
        city: business.city || "",
        email: business.email || "",
        phone: business.phone || "",
        google: business.google || "",
        linkedin: business.linkedin || "",
    });

    const set = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[field];
                return updated;
            });
        }
    };

    // Derived helpers
    const getSubcategories = (cat) => {
        const found = industriesData.find((c) => c.category === cat);
        return found ? found.subcategories : [];
    };
    const getItems = (sub) => {
        const cat = industriesData.find((c) => c.category === selectedCategory);
        const found = cat?.subcategories.find((s) => s.name === sub);
        return found ? found.items : [];
    };

    // File upload handler
    const uploadFile = (event, type) => {
        if (type === "images") {
            const files = Array.from(event.target.files || []);
            files.forEach((file) => {
                const filename = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
                S3FileUpload.uploadFile(new File([file], filename), config)
                    .then((data) => {
                        const url = data.location;
                        setBusinessImages((prev) => [
                            ...prev,
                            { name: file.name, image: url },
                        ]);
                    })
                    .catch((err) => {
                        console.error(err);
                        warningSwal("Error uploading image.");
                    });
            });
            return;
        }
        const file = event.target.files?.[0];
        if (!file) return;
        const ext = file.name.split(".").pop();
        const filename = `${Date.now()}.${ext}`;
        S3FileUpload.uploadFile(new File([file], filename), config)
            .then((data) => {
                const key = data.location.split("/").pop();
                setForm((prev) => ({
                    ...prev,
                    [type === "image" ? "business_image" : "document"]: key,
                }));
            })
            .catch((err) => {
                console.error(err);
                warningSwal("Error uploading file.");
            });
    };

    const removeImage = (idx) =>
        setBusinessImages((prev) => prev.filter((_, i) => i !== idx));

    const updateImageName = (idx, val) =>
        setBusinessImages((prev) => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], name: val };
            return copy;
        });

    const validateFields = () => {
        const newErrors = {};

        const fieldLabels = {
            business_name: "Business Name",
            sale_price: "Sale Price",
            email: "Email",
            phone: "Phone",
            city: "City",
            state: "State",
            about_company: "About Company",
            vintage_year: "Vintage Year",
            operation_type: "Operation Type",
            legal_entity: "Legal Entity",
        };

        const requiredFields = Object.keys(fieldLabels);

        requiredFields.forEach((field) => {
            const value = form[field];
            if (
                value === undefined ||
                value === null ||
                (typeof value === "string" && value.trim() === "") ||
                (Array.isArray(value) && value.length === 0)
            ) {
                newErrors[field] = `${fieldLabels[field]} is required.`;
            }
        });

        // Phone Number Format Validation
        if (!newErrors.phone) {
            const rawPhone = form.phone ? String(form.phone).trim() : "";
            let cleanPhone = rawPhone.replace(/[\s\-()]/g, "");
            if (cleanPhone.startsWith("+91")) {
                cleanPhone = cleanPhone.slice(3);
            } else if (cleanPhone.startsWith("91") && cleanPhone.length > 10) {
                cleanPhone = cleanPhone.slice(2);
            } else if (cleanPhone.startsWith("0") && cleanPhone.length > 10) {
                cleanPhone = cleanPhone.slice(1);
            }

            const phoneRegex = /^[6-9]\d{9}$/;
            if (!phoneRegex.test(cleanPhone)) {
                newErrors.phone = "Please enter a valid 10-digit phone number.";
            }
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const firstMissingField = Object.keys(newErrors)[0];
            const fieldElement = document.querySelector(
                `[name="${firstMissingField}"]`
            );
            if (fieldElement) {
                fieldElement.focus();
            }
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        const isValid = validateFields();
        if (!isValid) return;

        setSaving(true);
        try {
            const industry = [selectedCategory, selectedSubcategory, selectedItem].filter(Boolean);
            const body = {
                business_name: form.business_name,
                roc: form.roc,
                authorised_capital: form.authorised_capital !== "" ? Number(form.authorised_capital) : "",
                paid_up_capital: form.paid_up_capital !== "" ? Number(form.paid_up_capital) : "",
                turn_over: form.turn_over !== "" ? Number(form.turn_over) : "",
                pat: form.pat !== "" ? Number(form.pat) : "",
                location: form.location,
                vintage_year: form.vintage_year ? parseInt(form.vintage_year, 10) : "",
                date_of_incorporation: form.date_of_incorporation,
                document: form.document ? form.document.split("/").pop() : "",
                business_image: form.business_image ? form.business_image.split("/").pop() : "",
                about_company: form.about_company,
                industry,
                legal_entity: form.legal_entity,
                sale_price: form.sale_price !== "" ? Number(form.sale_price) : "",
                operation_type: form.operation_type,
                with_website: form.with_website !== "" ? parseInt(form.with_website, 10) : "",
                state: form.state,
                city: form.city,
                email: form.email,
                phone: form.phone,
                google: form.google,
                linkedin: form.linkedin,
                businessImages: businessImages.map((img) => ({
                    name: img.name,
                    image: img.image,
                })),
            };

            await updateBusinessForSale(business.CIN, body);
            SuccessSwal("Business listing updated successfully!");
            onUpdated();
            onClose();
        } catch (err) {
            console.error("Update error:", err);
            warningSwal(err?.response?.data?.message || "Failed to update. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onRequestClose={onClose}
            contentLabel="Edit Business for Sale"
            className={`${styles.modalContent} ${Theme !== "default" ? "green" : ""}`}
            overlayClassName={styles.modalOverlay}>
            {/* Header */}
            <div className={styles.modalHeader}>
                <div>
                    <h2>Edit Business Listing</h2>
                    <p>
                        Update the details for{" "}
                        <strong>{business.business_name}</strong> (CIN:{" "}
                        {business.CIN})
                    </p>
                </div>
                <button className={styles.closeBtn} onClick={onClose}>
                    &times;
                </button>
            </div>

            {/* Body */}
            <div className={styles.modalBody}>
                {/* Basic Info */}
                <h4 className={styles.sectionTitle}>Basic Information</h4>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Business Name *</label>
                        <input
                            type="text"
                            name="business_name"
                            value={form.business_name}
                            onChange={set("business_name")}
                            className={errors.business_name ? styles.errorInput : ""}
                            placeholder="Business Legal Name"
                        />
                        {errors.business_name && (
                            <span className={styles.errorMessage}>{errors.business_name}</span>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Legal Entity *</label>
                        <select
                            name="legal_entity"
                            value={form.legal_entity}
                            onChange={set("legal_entity")}
                            className={errors.legal_entity ? styles.errorInput : ""}>
                            <option value="">Select Entity Type</option>
                            <option value="Private Limited Company">Private Limited Company</option>
                            <option value="Public Limited Company">Public Limited Company</option>
                            <option value="Limited Liability Partnership">Limited Liability Partnership</option>
                            <option value="One Person Company">One Person Company</option>
                            <option value="Partnership">Partnership</option>
                            <option value="NBFC Companies">NBFC Companies</option>
                            <option value="Proprietorship">Proprietorship</option>
                        </select>
                        {errors.legal_entity && (
                            <span className={styles.errorMessage}>{errors.legal_entity}</span>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>ROC</label>
                        <input
                            type="text"
                            value={form.roc}
                            onChange={set("roc")}
                            placeholder="e.g. ROC Mumbai"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Vintage Year *</label>
                        <input
                            type="number"
                            name="vintage_year"
                            value={form.vintage_year}
                            onChange={set("vintage_year")}
                            className={errors.vintage_year ? styles.errorInput : ""}
                            onWheel={(e) => e.target.blur()}
                            placeholder="e.g. 2015"
                        />
                        {errors.vintage_year && (
                            <span className={styles.errorMessage}>{errors.vintage_year}</span>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Date of Incorporation</label>
                        <input
                            type="date"
                            value={form.date_of_incorporation}
                            onChange={set("date_of_incorporation")}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Asking Sale Price (₹) *</label>
                        <input
                            type="number"
                            name="sale_price"
                            value={form.sale_price}
                            onChange={set("sale_price")}
                            className={errors.sale_price ? styles.errorInput : ""}
                            onWheel={(e) => e.target.blur()}
                            placeholder="Enter asking price"
                        />
                        {errors.sale_price && (
                            <span className={styles.errorMessage}>{errors.sale_price}</span>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Official Email *</label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={set("email")}
                            className={errors.email ? styles.errorInput : ""}
                            placeholder="contact@company.com"
                        />
                        {errors.email && (
                            <span className={styles.errorMessage}>{errors.email}</span>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Phone *</label>
                        <input
                            type="text"
                            name="phone"
                            value={form.phone}
                            onChange={set("phone")}
                            className={errors.phone ? styles.errorInput : ""}
                            placeholder="Enter phone number"
                        />
                        {errors.phone && (
                            <span className={styles.errorMessage}>{errors.phone}</span>
                        )}
                    </div>
                </div>

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>LinkedIn URL</label>
                        <input
                            type="text"
                            value={form.linkedin}
                            onChange={set("linkedin")}
                            placeholder="LinkedIn profile URL"
                        />
                    </div>
                </div>

                {/* Industry */}
                <h4 className={styles.sectionTitle}>Industry & Sector</h4>
                <div className={styles.formGroup}>
                    <label>Industry Category</label>
                    <select
                        value={selectedCategory}
                        onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setSelectedSubcategory("");
                            setSelectedItem("");
                        }}>
                        <option value="">Select Industry</option>
                        {industriesData.map((cat, i) => (
                            <option key={i} value={cat.category}>
                                {cat.category}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Subcategory</label>
                        <select
                            value={selectedSubcategory}
                            disabled={!selectedCategory}
                            onChange={(e) => {
                                setSelectedSubcategory(e.target.value);
                                setSelectedItem("");
                            }}>
                            <option value="">Select Subcategory</option>
                            {getSubcategories(selectedCategory).map((s, i) => (
                                <option key={i} value={s.name}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Segment / Item</label>
                        <select
                            value={selectedItem}
                            disabled={!selectedSubcategory}
                            onChange={(e) => setSelectedItem(e.target.value)}>
                            <option value="">Select Item</option>
                            {getItems(selectedSubcategory).map((item, i) => (
                                <option key={i} value={item}>
                                    {item}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Financials */}
                <h4 className={styles.sectionTitle}>Financial Summary</h4>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Authorised Capital (₹)</label>
                        <input
                            type="number"
                            value={form.authorised_capital}
                            onChange={set("authorised_capital")}
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Paid-up Capital (₹)</label>
                        <input
                            type="number"
                            value={form.paid_up_capital}
                            onChange={set("paid_up_capital")}
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                </div>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Annual Turnover (₹)</label>
                        <input
                            type="number"
                            value={form.turn_over}
                            onChange={set("turn_over")}
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>PAT (Profit After Tax) (₹)</label>
                        <input
                            type="number"
                            value={form.pat}
                            onChange={set("pat")}
                            onWheel={(e) => e.target.blur()}
                        />
                    </div>
                </div>

                {/* Description */}
                <h4 className={styles.sectionTitle}>Business Description</h4>
                <div className={styles.formGroup}>
                    <label>About Company *</label>
                    <ReactQuill
                        theme="snow"
                        value={form.about_company}
                        onChange={(content) => {
                            setForm((prev) => ({ ...prev, about_company: content }));
                            if (errors.about_company) setErrors((prev) => ({ ...prev, about_company: "" }));
                        }}
                        placeholder="Describe your business..."
                        style={{
                            borderRadius: "12px",
                            background: "#fff",
                            border: errors.about_company ? "1px solid #ef4444" : "",
                        }}
                    />
                    {errors.about_company && (
                        <span className={styles.errorMessage}>{errors.about_company}</span>
                    )}
                </div>

                {/* Location */}
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>State *</label>
                        <input
                            type="text"
                            name="state"
                            value={form.state}
                            onChange={set("state")}
                            className={errors.state ? styles.errorInput : ""}
                            placeholder="State"
                        />
                        {errors.state && (
                            <span className={styles.errorMessage}>{errors.state}</span>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>City *</label>
                        <input
                            type="text"
                            name="city"
                            value={form.city}
                            onChange={set("city")}
                            className={errors.city ? styles.errorInput : ""}
                            placeholder="City"
                        />
                        {errors.city && (
                            <span className={styles.errorMessage}>{errors.city}</span>
                        )}
                    </div>
                </div>
                <div className={styles.formGroup}>
                    <label>Registered Address</label>
                    <textarea
                        value={form.location}
                        onChange={set("location")}
                        placeholder="Full registered office address"
                    />
                </div>

                {/* Operation */}
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Operation Type *</label>
                        <select
                            name="operation_type"
                            value={form.operation_type}
                            onChange={set("operation_type")}
                            className={errors.operation_type ? styles.errorInput : ""}>
                            <option value="">Select Operation Type</option>
                            <option value="Manufacturing">Manufacturing</option>
                            <option value="Services">Services</option>
                            <option value="Retail">Retail</option>
                            <option value="Trading">Trading</option>
                        </select>
                        {errors.operation_type && (
                            <span className={styles.errorMessage}>{errors.operation_type}</span>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Presence Online</label>
                        <select value={form.with_website} onChange={set("with_website")}>
                            <option value="">Select Website Option</option>
                            <option value="1">With Website</option>
                            <option value="0">Without Website</option>
                        </select>
                    </div>
                </div>

                {form.with_website === "1" && (
                    <div className={styles.formGroup}>
                        <label>Website URL</label>
                        <input
                            type="text"
                            value={form.google}
                            onChange={set("google")}
                            placeholder="https://example.com"
                        />
                    </div>
                )}

                {/* Media */}
                <h4 className={styles.sectionTitle}>Images & Documents</h4>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>Business Main Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadFile(e, "image")}
                        />
                        {form.business_image && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                                <img
                                    src={
                                        form.business_image.startsWith("http")
                                            ? form.business_image
                                            : `https://${process.env.REACT_APP_BUCKETNAME}.s3.ap-south-1.amazonaws.com/${form.business_image}`
                                    }
                                    alt="Preview"
                                    style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "8px" }}
                                />
                                <span style={{ fontSize: "12px", color: "#64748b" }}>Current image</span>
                            </div>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <label>Relevant Document (PDF/Word)</label>
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => uploadFile(e, "document")}
                        />
                        {form.document && (
                            <p style={{ fontSize: "12px", color: "#10b981", marginTop: "8px" }}>
                                Document uploaded ✓
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label>Additional Business Photos</label>
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => uploadFile(e, "images")}
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "12px", marginTop: "16px" }}>
                        {businessImages.map((img, index) => (
                            <div
                                key={index}
                                style={{ position: "relative", border: "1px solid #e2e8f0", padding: "8px", borderRadius: "12px", background: "#f8fafc" }}>
                                <img
                                    src={
                                        img.image.startsWith("http")
                                            ? img.image
                                            : `https://${process.env.REACT_APP_BUCKETNAME}.s3.ap-south-1.amazonaws.com/${img.image}`
                                    }
                                    style={{ width: "100%", height: "80px", objectFit: "cover", borderRadius: "8px" }}
                                    alt={`preview-${index}`}
                                />
                                <input
                                    type="text"
                                    value={img.name}
                                    onChange={(e) => updateImageName(index, e.target.value)}
                                    placeholder="Caption"
                                    style={{ fontSize: "11px", height: "auto", padding: "4px", marginTop: "4px", border: "none", background: "transparent", width: "100%" }}
                                />
                                <button
                                    onClick={() => removeImage(index)}
                                    style={{ position: "absolute", top: "-8px", right: "-8px", background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: "20px", height: "20px", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={styles.modalFooter}>
                <button className={styles.secondaryBtn} onClick={onClose}>
                    Cancel
                </button>
                <button
                    className={styles.primaryBtn}
                    onClick={handleSave}
                    disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </Modal>
    );
};

export default EditBusinessModal;
